// src/pages/AgentDashboard.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  Button,
  Card,
  Col,
  Row,
  Table,
  Badge,
  ProgressBar,
  ButtonGroup,
  Spinner,
  Placeholder,
  Form,
} from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import {
  CurrencyDollar,
  FileEarmarkExcel,
  People,
  GraphUp,
  Clock,
  Receipt,
  Building,
  Calendar,
  ArrowUp,
  ArrowDown,
  Cash,
  CreditCard,
  Phone,
  ArrowUpRight,
  Eye,
  CheckCircle,
  Activity,
  BarChart,
  PieChart,
  Globe,
  ExclamationCircle,
  CashStack,
  Bank,
  Wallet,
  ClockHistory,
  X,
  Repeat,
  Trophy,
  GeoAlt,
  Briefcase,
  ArrowRepeat,
  InfoCircle,
  Shop,
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../constants/api_constants";
import {
  getUserRoleId,
  getUserRoles,
  isSuperAdmin as checkSuperAdmin,
  canFilterByMarketScope,
} from "../utils/auth";

const ALLOWED_MARKET_SCOPE_ROLES = [1002];

let dashboardCache = {
  data: null,
  timestamp: null,
  ttl: 300000,
};

export default function AgentDashboard() {
  const defaultCurrency = sessionStorage.getItem("default_currency") || "XOF";

  const userRoleId = useMemo(() => {
    const id = getUserRoleId();
    return id || 0;
  }, []);
  const isSuperAdmin = useMemo(() => checkSuperAdmin(), []);
  const isAdminEntite = userRoleId === 1002;

  const canUseMarketScope = useMemo(() => {
    if (isSuperAdmin === true) return true;
    if (ALLOWED_MARKET_SCOPE_ROLES.includes(userRoleId)) return true;
    return false;
  }, [isSuperAdmin, userRoleId]);

  const [dashboardData, setDashboardData] = useState({
    stats_globaux: null,
    contribuables: { data: [] },
    collectes: { stats: {}, evolution: [] },
    enrollement: { global: null, parAgent: [], totauxAgents: null },
  });

  const [stats, setStats] = useState({
    totalContribuables: 0,
    totalAttendu: 0,
    totalPaye: 0,
    totalRestant: 0,
    tauxRecouvrement: 0,
    registeredToday: 0,
    echeancesPayees: 0,
    echeancesPartielles: 0,
    echeancesEnAttente: 0,
  });

  const [quartierStats, setQuartierStats] = useState([]);
  const [loadingQuartierStats, setLoadingQuartierStats] = useState(true);
  const [quartierStatsOrder, setQuartierStatsOrder] = useState("total");
  const [localitesMap, setLocalitesMap] = useState({});
  const [loadingLocalites, setLoadingLocalites] = useState(false);

  const [timeRange, setTimeRange] = useState("month");
  const [marketScope, setMarketScope] = useState("all");

  // ✅ Filtres de dates personnalisées
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
    period: "month",
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [topTaxpayers, setTopTaxpayers] = useState([]);
  const [taxDistribution, setTaxDistribution] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingTopContribuables, setLoadingTopContribuables] = useState(true);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [loadingCharts, setLoadingCharts] = useState(true);
  const [loadingRefresh, setLoadingRefresh] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [error, setError] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState({
    series: [],
    categories: [],
  });
  const [contribuableGrowth, setContribuableGrowth] = useState({
    series: [],
    categories: [],
  });
  const [totalTaxAmounts, setTotalTaxAmounts] = useState({
    paid: 0,
    expected: 0,
  });

  const getAuthToken = () => sessionStorage.getItem("authToken");

  const getHeaders = () => {
    const token = getAuthToken();
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return (
      numAmount.toLocaleString("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }) +
      " " +
      defaultCurrency
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatDateForAPI = (date) => {
    if (!date) return "";
    return new Date(date).toISOString().split("T")[0];
  };

  // ✅ Met à jour la période rapide
  const updatePeriod = useCallback((period) => {
    const endDate = new Date();
    const startDate = new Date();

    if (period === "day") {
      // Aujourd'hui : start = end = today
      setDateRange({
        startDate: formatDateForAPI(endDate),
        endDate: formatDateForAPI(endDate),
        period,
      });
      return;
    }

    if (period === "week") startDate.setDate(startDate.getDate() - 7);
    else if (period === "month") startDate.setDate(startDate.getDate() - 30);
    else if (period === "year") startDate.setFullYear(startDate.getFullYear() - 1);
    else if (period === "all") {
      setDateRange({ startDate: "", endDate: "", period: "all" });
      return;
    }

    setDateRange({
      startDate: formatDateForAPI(startDate),
      endDate: formatDateForAPI(endDate),
      period,
    });
  }, []);

  // ✅ Réinitialise
  const resetDateRange = useCallback(() => {
    updatePeriod("month");
  }, [updatePeriod]);

  const clearCache = () => {
    dashboardCache = {
      data: null,
      timestamp: null,
      ttl: 300000,
    };
  };

  const exportQuartierStatsToExcel = () => {
    if (!quartierStats || quartierStats.length === 0) {
      alert("Aucune donnée à exporter.");
      return;
    }

    const excelData = quartierStats.map((stat, index) => ({
      "N°": index + 1,
      Quartier: stat.quartier_nom || "-",
      "Nombre de contribuables": Number(stat.contribuables || 0),
      "Taxe prévue": Number(stat.totalTaxe || 0),
      "Montant collecté": Number(stat.totalCollecte || 0),
      "Reste à payer": Number(stat.totalReste || 0),
      "Taux de recouvrement (%)": Number((stat.tauxRecouvrement || 0).toFixed(2)),
    }));

    const totalContribuables = quartierStats.reduce(
      (sum, stat) => sum + Number(stat.contribuables || 0),
      0
    );
    const totalTaxe = quartierStats.reduce((sum, stat) => sum + Number(stat.totalTaxe || 0), 0);
    const totalCollecte = quartierStats.reduce(
      (sum, stat) => sum + Number(stat.totalCollecte || 0),
      0
    );
    const totalReste = quartierStats.reduce((sum, stat) => sum + Number(stat.totalReste || 0), 0);
    const tauxGlobal =
      totalTaxe > 0 ? Number(((totalCollecte / totalTaxe) * 100).toFixed(2)) : 0;

    excelData.push({
      "N°": "",
      Quartier: "TOTAL GÉNÉRAL",
      "Nombre de contribuables": totalContribuables,
      "Taxe prévue": totalTaxe,
      "Montant collecté": totalCollecte,
      "Reste à payer": totalReste,
      "Taux de recouvrement (%)": tauxGlobal,
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    worksheet["!cols"] = [
      { wch: 8 },
      { wch: 30 },
      { wch: 24 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 24 },
    ];

    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      ["C", "D", "E", "F"].forEach((col) => {
        const cell = worksheet[`${col}${row + 1}`];
        if (cell && typeof cell.v === "number") cell.z = "#,##0";
      });
      const tauxCell = worksheet[`G${row + 1}`];
      if (tauxCell && typeof tauxCell.v === "number") tauxCell.z = '0.00"%"';
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Statistiques Quartiers");
    const dateExport = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Statistiques_Quartiers_${dateExport}.xlsx`);
  };

  const fetchLocalites = useCallback(async () => {
    try {
      setLoadingLocalites(true);
      const response = await axios.get(`${API_BASE_URL}/localites`, {
        headers: getHeaders(),
      });

      let allLocalites = [];
      if (response.data && Array.isArray(response.data)) allLocalites = response.data;
      else if (response.data && response.data.data && Array.isArray(response.data.data))
        allLocalites = response.data.data;

      const map = {};
      allLocalites.forEach((l) => {
        map[String(l.localite_id)] = l;
      });
      setLocalitesMap(map);
      return map;
    } catch (error) {
      console.error("❌ Erreur chargement localités:", error);
      return {};
    } finally {
      setLoadingLocalites(false);
    }
  }, []);

  const getQuartierFromLocalite = useCallback((localiteId, map) => {
    if (!localiteId || !map) return null;
    const idStr = String(localiteId);
    const loc = map[idStr];
    if (!loc) return null;
    if (loc.type_code === 1002) return loc;
    return null;
  }, []);

  const loadQuartierStats = useCallback(async () => {
    try {
      setLoadingQuartierStats(true);

      let map = localitesMap;
      if (Object.keys(map).length === 0) {
        map = await fetchLocalites();
      }

      // ✅ Utilise dateRange au lieu de calculer depuis timeRange
      const response = await axios.get(`${API_BASE_URL}/rapports/complet`, {
        headers: getHeaders(),
        params: {
          contribuables_page: 1,
          contribuables_per_page: 10000,
          start_date: dateRange.startDate || "",
          end_date: dateRange.endDate || "",
          market_scope: canUseMarketScope ? marketScope : "all",
        },
      });

      if (response.data.success) {
        const contribuablesData = response.data.contribuables?.data || [];
        const statsByQuartier = {};

        for (const item of contribuablesData) {
          const localiteId = item.localite_id ? String(item.localite_id) : null;
          const quartier = localiteId ? getQuartierFromLocalite(localiteId, map) : null;

          if (quartier) {
            const key = String(quartier.localite_id);
            if (!statsByQuartier[key]) {
              statsByQuartier[key] = {
                quartier_id: quartier.localite_id,
                quartier_nom: quartier.nom,
                totalTaxe: 0,
                totalCollecte: 0,
                totalReste: 0,
                contribuables: 0,
                contribuablesAvecTaxe: 0,
              };
            }

            statsByQuartier[key].contribuables++;

            const totalTaxe = parseFloat(item.montantInitialTotal || item.montant_total || 0);
            const totalCollecte = parseFloat(item.montantPayeTotal || item.montant_paye || 0);

            statsByQuartier[key].totalTaxe += totalTaxe;
            statsByQuartier[key].totalCollecte += totalCollecte;
            if (totalTaxe > 0) {
              statsByQuartier[key].contribuablesAvecTaxe++;
            }
          }
        }

        const statsArray = Object.values(statsByQuartier).map((stat) => ({
          ...stat,
          totalReste: stat.totalTaxe - stat.totalCollecte,
          tauxRecouvrement:
            stat.totalTaxe > 0 ? (stat.totalCollecte / stat.totalTaxe) * 100 : 0,
        }));

        sortQuartierStats(statsArray, quartierStatsOrder);
        setQuartierStats(statsArray);
      }
    } catch (error) {
      console.error("❌ Erreur chargement statistiques par quartier:", error);
    } finally {
      setLoadingQuartierStats(false);
    }
  }, [
    getHeaders,
    fetchLocalites,
    localitesMap,
    getQuartierFromLocalite,
    quartierStatsOrder,
    dateRange.startDate,
    dateRange.endDate,
    marketScope,
    canUseMarketScope,
  ]);

  const sortQuartierStats = (stats, order) => {
    switch (order) {
      case "total":
        stats.sort((a, b) => b.totalTaxe - a.totalTaxe);
        break;
      case "collecte":
        stats.sort((a, b) => b.totalCollecte - a.totalCollecte);
        break;
      case "reste":
        stats.sort((a, b) => b.totalReste - a.totalReste);
        break;
      case "contribuables":
        stats.sort((a, b) => b.contribuables - a.contribuables);
        break;
      case "taux":
        stats.sort((a, b) => b.tauxRecouvrement - a.tauxRecouvrement);
        break;
      default:
        stats.sort((a, b) => b.totalTaxe - a.totalTaxe);
    }
  };

  const handleQuartierStatsOrderChange = (order) => {
    setQuartierStatsOrder(order);
    const sorted = [...quartierStats];
    sortQuartierStats(sorted, order);
    setQuartierStats(sorted);
  };

  const loadDashboardData = useCallback(
    async (forceRefresh = false) => {
      try {
        if (forceRefresh) {
          clearCache();
          setLoadingRefresh(true);
        } else {
          setLoading(true);
        }
        setError(null);

        const cacheKey = `dashboard_unified_${timeRange}_${dateRange.startDate}_${dateRange.endDate}_${
          canUseMarketScope ? marketScope : "all"
        }`;
        const cachedData = dashboardCache[cacheKey];
        const now = Date.now();

        if (
          !forceRefresh &&
          cachedData &&
          now - cachedData.timestamp < dashboardCache.ttl
        ) {
          applyDashboardData(cachedData.data);
          setLoading(false);
          setLoadingRefresh(false);
          setLoadingFilter(false);
          return;
        }

        // ✅ Utilise dateRange au lieu de timeRange pour start/end
        const params = {
          start_date: dateRange.startDate || "",
          end_date: dateRange.endDate || "",
          market_scope: canUseMarketScope ? marketScope : "all",
          contribuables_page: 1,
          contribuables_per_page: 100,
          agents_page: 1,
          agents_per_page: 100,
        };

        console.log("📡 Requête API avec paramètres:", params);

        const response = await axios.get(`${API_BASE_URL}/rapports/complet`, {
          headers: getHeaders(),
          params,
        });

        if (response.data.success) {
          dashboardCache[cacheKey] = { data: response.data, timestamp: now };
          applyDashboardData(response.data);
        } else {
          console.error("API Error:", response.data);
          setError(response.data.message || "Erreur lors du chargement");
        }

        setLoading(false);
        setLoadingRefresh(false);
        setLoadingFilter(false);
      } catch (err) {
        console.error("Erreur chargement dashboard:", err);
        setError(err.response?.data?.message || err.message);
        setLoading(false);
        setLoadingRefresh(false);
        setLoadingFilter(false);
        setLoadingStats(false);
        setLoadingTopContribuables(false);
        setLoadingActivities(false);
        setLoadingCharts(false);
      }
    },
    [timeRange, marketScope, canUseMarketScope, dateRange.startDate, dateRange.endDate]
  );

  const handleTimeRangeChange = (newRange) => {
    if (newRange === timeRange && dateRange.period === newRange) return;
    setLoadingFilter(true);
    setTimeRange(newRange);
    updatePeriod(newRange);
  };

  const applyDashboardData = (data) => {
    const statsData = data.stats_globaux || {};
    const contribuablesList = data.contribuables?.data || [];
    const evolution = data.evolution || [];

    setStats({
      totalContribuables: statsData.totalContribuables || 0,
      totalAttendu: statsData.totalAttendu || 0,
      totalPaye: statsData.totalPaye || 0,
      totalRestant: statsData.totalRestant || 0,
      tauxRecouvrement: statsData.tauxRecouvrement || 0,
      registeredToday: statsData.registeredToday || 0,
      echeancesPayees: statsData.echeancesPayees || 0,
      echeancesPartielles: statsData.echeancesPartielles || 0,
      echeancesEnAttente: statsData.echeancesEnAttente || 0,
    });
    setLoadingStats(false);

    if (data.top_contribuables && data.top_contribuables.length > 0) {
      const top5 = data.top_contribuables.map((c) => ({
        id: c.id,
        name: c.name,
        type: c.type,
        amountPaid: formatCurrency(c.amountPaid),
        amountExpected: formatCurrency(c.amountExpected),
        paymentCount: c.paymentCount,
        percentagePaid: c.percentagePaid,
        subAccountsCount: c.subAccountsCount || 0,
      }));
      setTopTaxpayers(top5);
    } else {
      const top5 = [...contribuablesList]
        .sort((a, b) => b.montantInitialTotal - a.montantInitialTotal)
        .slice(0, 5)
        .map((c) => ({
          id: c.contribuable_id,
          name: c.nom,
          type: c.type || "Entreprise",
          amountPaid: formatCurrency(c.montantPayeTotal),
          amountExpected: formatCurrency(c.montantInitialTotal),
          paymentCount: c.nombreSouscriptions,
          percentagePaid: c.tauxPaiement,
          subAccountsCount: c.nbSousComptes || 0,
        }));
      setTopTaxpayers(top5);
    }
    setLoadingTopContribuables(false);

    if (data.recent_activities && data.recent_activities.length > 0) {
      const formattedActivities = data.recent_activities.map((a) => ({
        id: a.id,
        user: a.user,
        action: a.action,
        amount: formatCurrency(a.amount),
        time: a.time,
        status: a.status,
        reference: a.reference,
        agent: a.agent || "",
      }));
      setRecentActivities(formattedActivities);
    } else {
      const collectesList = data.collectes?.data || [];
      const formattedActivities = collectesList.slice(0, 5).map((p) => ({
        id: p.id,
        user: p.nom_contribuable,
        action: `Paiement - ${p.reference_paiement}`,
        amount: formatCurrency(p.montant),
        time: formatDate(p.created_at),
        status: p.statut === "completed" ? "success" : "pending",
        reference: p.reference_paiement,
        agent: "",
      }));
      setRecentActivities(formattedActivities);
    }
    setLoadingActivities(false);

    if (evolution && evolution.length > 0) {
      const objectifData = evolution.map((e) => e.montant * 1.1);
      setMonthlyRevenue({
        series: [
          { name: "Collecté", data: evolution.map((e) => e.montant) },
          { name: "Objectif ", data: objectifData },
        ],
        categories: evolution.map((e) => e.mois),
      });
    }

    if (data.payment_methods && data.payment_methods.length > 0) {
      const formattedMethods = data.payment_methods.map((m) => ({
        method: m.method,
        method_key: m.method_key,
        count: m.count,
        amount: formatCurrency(m.amount),
        percentage: m.percentage,
        amount_percentage: m.amount_percentage,
        color: m.color,
      }));
      setPaymentMethods(formattedMethods);
    }

    if (data.tax_distribution && data.tax_distribution.length > 0) {
      const formattedTaxes = data.tax_distribution.map((t) => ({
        type: t.type,
        taxe_id: t.taxe_id,
        amount: t.amount,
        amountExpected: t.amountExpected,
        payment_count: t.payment_count,
        percentage: t.percentage,
        color: t.color,
      }));
      setTaxDistribution(formattedTaxes);
      setTotalTaxAmounts({
        paid: data.tax_totals?.paid || 0,
        expected: data.tax_totals?.expected || 0,
      });
    }

    if (data.contribuable_growth && data.contribuable_growth.series) {
      setContribuableGrowth({
        series: data.contribuable_growth.series,
        categories:
          data.contribuable_growth.categories || [
            "Jan", "Fév", "Mar", "Avr", "Mai", "Juin",
            "Juil", "Aoû", "Sep", "Oct", "Nov", "Déc",
          ],
      });
    } else {
      setContribuableGrowth({
        series: [
          {
            name: "Nouveaux contribuables",
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
          },
        ],
        categories: [
          "Jan", "Fév", "Mar", "Avr", "Mai", "Juin",
          "Juil", "Aoû", "Sep", "Oct", "Nov", "Déc",
        ],
      });
    }

    setLoadingCharts(false);
  };

  const quartierTaxChartOptions = {
    chart: {
      type: "bar",
      height: 350,
      toolbar: {
        show: true,
        tools: {
          download: true, selection: true, zoom: true,
          zoomin: true, zoomout: true, pan: true, reset: true,
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        endingShape: "rounded",
        borderRadius: 4,
      },
    },
    dataLabels: {
      enabled: false,
      formatter: (val) => {
        const num = parseFloat(val) || 0;
        return num.toLocaleString("fr-FR", {
          minimumFractionDigits: 0, maximumFractionDigits: 0,
        });
      },
      style: { fontSize: "10px", fontWeight: "bold" },
    },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: {
      categories: quartierStats.map((q) => q.quartier_nom),
      labels: { rotate: -45, style: { fontSize: "11px" }, trim: false },
    },
    yaxis: {
      labels: {
        formatter: (val) => {
          const num = parseFloat(val) || 0;
          return num.toLocaleString("fr-FR", {
            minimumFractionDigits: 0, maximumFractionDigits: 0,
          });
        },
      },
      title: { text: `Montant (${defaultCurrency})` },
    },
    legend: { position: "top", horizontalAlign: "center" },
    colors: ["#0d6efd", "#198754", "#dc3545"],
    tooltip: {
      y: {
        formatter: (val) => {
          const num = parseFloat(val) || 0;
          return num.toLocaleString("fr-FR", {
            minimumFractionDigits: 0, maximumFractionDigits: 0,
          }) + " " + defaultCurrency;
        },
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: { chart: { height: 280 }, dataLabels: { enabled: false } },
      },
    ],
  };

  const quartierTaxSeries = [
    { name: "Taxe totale (prévu)", data: quartierStats.map((q) => q.totalTaxe) },
    { name: "Taxe collectée (payé)", data: quartierStats.map((q) => q.totalCollecte) },
    { name: "Reste à payer", data: quartierStats.map((q) => q.totalReste) },
  ];

  const quartierContribuablesChartOptions = {
    chart: { type: "bar", height: 250, toolbar: { show: false } },
    plotOptions: {
      bar: {
        horizontal: false, columnWidth: "50%",
        endingShape: "rounded", borderRadius: 4,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => val,
      style: { fontSize: "10px", fontWeight: "bold" },
    },
    xaxis: {
      categories: quartierStats.map((q) => q.quartier_nom),
      labels: { rotate: -45, style: { fontSize: "10px" }, trim: false },
    },
    yaxis: { title: { text: "Nombre" } },
    colors: ["#9b59b6"],
    tooltip: { y: { formatter: (val) => `${val} contribuable(s)` } },
    responsive: [
      {
        breakpoint: 768,
        options: { chart: { height: 200 }, dataLabels: { enabled: false } },
      },
    ],
  };

  const quartierContribuablesSeries = [
    { name: "Contribuables", data: quartierStats.map((q) => q.contribuables) },
  ];

  const revenueChartOptions = {
    chart: {
      type: "area",
      height: 550,
      toolbar: {
        show: true,
        tools: {
          download: true, selection: true, zoom: true,
          zoomin: true, zoomout: true, pan: true, reset: true,
        },
      },
      zoom: { enabled: true, type: "x", autoScaleYaxis: true },
      animations: { enabled: true, easing: "easeinout", speed: 800 },
      background: "transparent",
    },
    colors: ["#2ecc71", "#9b59b6"],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 0.5, opacityFrom: 0.8,
        opacityTo: 0.2, stops: [0, 90, 100],
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => {
        if (val === 0) return "0";
        const formatted = val.toLocaleString("fr-FR");
        return `${formatted} ${defaultCurrency}`;
      },
      offsetY: -15,
      style: { fontSize: "11px", fontWeight: "bold", colors: ["#2ecc71", "#9b59b6"] },
    },
    stroke: { show: true, width: 3, curve: "smooth" },
    markers: {
      size: 5, colors: ["#2ecc71", "#9b59b6"],
      strokeColors: "#ffffff", strokeWidth: 2,
    },
    grid: { borderColor: "#e0e6ed", strokeDashArray: 3 },
    xaxis: {
      categories: monthlyRevenue.categories,
      labels: { style: { colors: "#495057", fontSize: "12px", fontWeight: 600 } },
      title: { text: "Mois" },
    },
    yaxis: {
      labels: {
        formatter: (value) => {
          if (value === 0) return "0";
          return `${value.toLocaleString("fr-FR")} ${defaultCurrency}`;
        },
      },
      min: 0,
      title: { text: `Montant (${defaultCurrency})` },
      forceNiceScale: true,
      decimalsInFloat: 0,
      tickAmount: 6,
    },
    legend: { position: "top", horizontalAlign: "center" },
    tooltip: { enabled: true, shared: true, intersect: false },
  };

  const growthChartOptions = {
    chart: { type: "line", height: 200, toolbar: { show: false } },
    colors: ["#9b59b6"],
    stroke: { curve: "smooth", width: 3 },
    dataLabels: { enabled: false },
    grid: { borderColor: "#e0e6ed", strokeDashArray: 3 },
    xaxis: {
      categories: contribuableGrowth.categories,
      labels: { style: { colors: "#6c757d" } },
    },
    yaxis: { labels: { style: { colors: "#6c757d" } } },
    tooltip: { y: { formatter: (value) => `${value} nouveaux contribuables` } },
  };

  const deviceUsageOptions = {
    chart: { type: "donut", height: 300 },
    labels: paymentMethods.map((p) => p.method),
    colors: paymentMethods.map((p) => p.color || "#3498db"),
    legend: { position: "bottom", labels: { colors: "#6c757d" } },
    plotOptions: {
      pie: {
        donut: {
          size: "65%",
          labels: {
            show: true,
            name: { show: true },
            value: { show: true, formatter: (val) => `${val}%` },
            total: { show: true, label: "Total", formatter: () => "100%" },
          },
        },
      },
    },
    dataLabels: { enabled: false },
    tooltip: {
      y: {
        formatter: (val, { seriesIndex }) =>
          `${paymentMethods[seriesIndex]?.amount_percentage || val}% des montants`,
      },
    },
  };

  const deviceUsageSeries = paymentMethods.map((p) => p.percentage);

  const performanceChartOptions = {
    chart: { type: "radialBar", height: 280 },
    plotOptions: {
      radialBar: {
        startAngle: -90,
        endAngle: 270,
        hollow: { margin: 0, size: "70%", background: "transparent" },
        dataLabels: {
          name: { show: false },
          value: { color: "#111", fontSize: "30px", show: true, offsetY: 10 },
        },
      },
    },
    colors: ["#2ecc71"],
    stroke: { lineCap: "round" },
    labels: ["Performance"],
  };

  const performanceChartSeries = [stats.tauxRecouvrement];

  const getStatusBadge = (status) => {
    const config = {
      success: { color: "success", text: "Payé", icon: <CheckCircle className="me-1" /> },
      pending: { color: "warning", text: "En attente", icon: <Clock className="me-1" /> },
      completed: { color: "success", text: "Réussi", icon: <CheckCircle className="me-1" /> },
      failed: { color: "danger", text: "Échoué", icon: <ExclamationCircle className="me-1" /> },
      danger: { color: "danger", text: "Échoué", icon: <ExclamationCircle className="me-1" /> },
      warning: { color: "warning", text: "En attente", icon: <Clock className="me-1" /> },
      en_attente: { color: "warning", text: "En attente", icon: <Clock className="me-1" /> },
    };
    const badgeConfig = config[status?.toLowerCase()] || {
      color: "secondary", text: status, icon: <Clock className="me-1" />,
    };
    return (
      <Badge bg={badgeConfig.color} className="d-flex align-items-center px-2 py-1 rounded-pill">
        {badgeConfig.icon}
        {badgeConfig.text}
      </Badge>
    );
  };

  const getPaymentMethodIcon = (method) => {
    switch (method?.toLowerCase()) {
      case "mobile money":
      case "orange money":
      case "mtn mobile money":
      case "free money":
      case "wave":
        return <Phone className="me-2" />;
      case "carte bancaire":
      case "card":
        return <CreditCard className="me-2" />;
      case "espèces":
      case "cash":
      case "especes":
        return <CashStack className="me-2" />;
      case "virement":
      case "bank":
      case "bank_transfer":
      case "virement bancaire":
        return <Bank className="me-2" />;
      default:
        return <Wallet className="me-2" />;
    }
  };

  const StatCard = ({
    title, value, icon: Icon, color, trend, trendValue, description, loading,
  }) => (
    <Card className="border-0 shadow-sm h-100 bg-white position-relative overflow-hidden stats-card">
      <div
        className={`position-absolute top-0 start-0 w-100 bg-${color}`}
        style={{
          height: "4px",
          background: `linear-gradient(90deg, var(--bs-${color}), var(--bs-${color}-light))`,
        }}
      />
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div
            className="d-flex align-items-center justify-content-center rounded-3"
            style={{
              width: 38, height: 38,
              backgroundColor: `rgba(var(--bs-${color}-rgb), 0.1)`,
            }}
          >
            <Icon className={`text-${color}`} size={19} strokeWidth={1.5} />
          </div>
          {trend && !loading && (
            <div
              className={`d-flex align-items-center px-2 py-1 rounded-pill ${
                trend === "up" ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"
              }`}
              style={{ fontSize: "11px", fontWeight: 500 }}
            >
              {trend === "up" ? <ArrowUp size={10} className="me-1" /> : <ArrowDown size={10} className="me-1" />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="mt-2">
            <Placeholder as="div" animation="glow">
              <Placeholder xs={6} style={{ height: "30px", borderRadius: "8px" }} className="mb-2" />
              <Placeholder xs={8} style={{ height: "14px", borderRadius: "4px" }} />
              {description && <Placeholder xs={10} style={{ height: "12px", borderRadius: "4px" }} className="mt-2" />}
            </Placeholder>
          </div>
        ) : (
          <>
            <div className="mt-2 d-flex align-items-baseline flex-wrap">
              <span className="fw-bold" style={{ fontSize: "19px", color: "#0f172a", lineHeight: 1.2 }}>
                {typeof value === "number" ? value.toLocaleString("fr-FR") : value}
              </span>
            </div>
            <div className="mt-1">
              <span
                className="text-uppercase fw-semibold"
                style={{ fontSize: "11px", color: "#64748b", letterSpacing: "0.5px" }}
              >
                {title}
              </span>
            </div>
            {description && (
              <p className="text-muted mb-0 mt-2" style={{ fontSize: "11px" }}>
                {description}
              </p>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );

  // ✅ Init par défaut
  useEffect(() => {
    updatePeriod("month");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Chargement initial
  useEffect(() => {
    const initialize = async () => {
      await loadDashboardData(false);
      await fetchLocalites();
      await loadQuartierStats();
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Recharge quand les dates ou le périmètre changent
  useEffect(() => {
    if (dateRange.startDate || dateRange.endDate || dateRange.period === "all") {
      loadDashboardData(false);
      loadQuartierStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate, marketScope]);

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: "#f5f7fa", minHeight: "100vh" }}>
        {/* En-tête */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
          <div className="mb-3 mb-md-0">
            <h4 className="main-title mb-1">Tableau de bord</h4>
            <p className="text-muted mb-0">
              <Calendar className="me-2" />
              {dateRange.startDate && dateRange.endDate
                ? dateRange.startDate === dateRange.endDate
                  ? `Le ${new Date(dateRange.startDate).toLocaleDateString("fr-FR")}`
                  : `Du ${new Date(dateRange.startDate).toLocaleDateString("fr-FR")} au ${new Date(dateRange.endDate).toLocaleDateString("fr-FR")}`
                : "Toutes périodes"}{" "}
              • {new Date().toLocaleDateString("fr-FR")}
              {canUseMarketScope && (
                <Badge
                  bg={
                    marketScope === "all" ? "secondary"
                    : marketScope === "no_market" ? "primary" : "success"
                  }
                  className="ms-2 px-3 py-2"
                >
                  <Shop className="me-1" size={12} />
                  {isSuperAdmin ? "Super Admin • " : ""}
                  {marketScope === "all" ? "Tous"
                    : marketScope === "no_market" ? "Hors marché" : "De marché"}
                </Badge>
              )}
            </p>
          </div>
        </div>

        {/* ✅ Barre de filtres dates */}
        <Row className="mb-4">
          <Col xs={12}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: "12px" }}>
              <Card.Body className="py-3">
                <Row className="align-items-center g-3">
                  <Col md={4}>
                    <Form.Label className="fw-semibold mb-1 small">Période rapide</Form.Label>
                    <ButtonGroup size="sm" className="w-100 flex-wrap">
                      <Button
                        variant={dateRange.period === "all" ? "primary" : "outline-primary"}
                        onClick={() => handleTimeRangeChange("all")}
                        disabled={loadingFilter}
                      >
                        Toutes
                      </Button>
                      <Button
                        variant={dateRange.period === "day" ? "primary" : "outline-primary"}
                        onClick={() => handleTimeRangeChange("day")}
                        disabled={loadingFilter}
                      >
                        Aujourd'hui
                      </Button>
                      <Button
                        variant={dateRange.period === "week" ? "primary" : "outline-primary"}
                        onClick={() => handleTimeRangeChange("week")}
                        disabled={loadingFilter}
                      >
                        7 j
                      </Button>
                      <Button
                        variant={dateRange.period === "month" ? "primary" : "outline-primary"}
                        onClick={() => handleTimeRangeChange("month")}
                        disabled={loadingFilter}
                      >
                        30 j
                      </Button>
                      <Button
                        variant={dateRange.period === "year" ? "primary" : "outline-primary"}
                        onClick={() => handleTimeRangeChange("year")}
                        disabled={loadingFilter}
                      >
                        1 an
                      </Button>
                    </ButtonGroup>
                  </Col>

                  <Col md={2}>
                    <Form.Label className="fw-semibold mb-1 small">Date de début</Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={dateRange.startDate}
                      onChange={(e) =>
                        setDateRange((prev) => ({
                          ...prev,
                          startDate: e.target.value,
                          period: "custom",
                        }))
                      }
                      disabled={loadingFilter}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fw-semibold mb-1 small">Date de fin</Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={dateRange.endDate}
                      onChange={(e) =>
                        setDateRange((prev) => ({
                          ...prev,
                          endDate: e.target.value,
                          period: "custom",
                        }))
                      }
                      disabled={loadingFilter}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="fw-semibold mb-1 small">&nbsp;</Form.Label>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="w-100"
                      onClick={resetDateRange}
                      disabled={loadingFilter}
                    >
                      <ClockHistory className="me-1" size={14} /> Réinitialiser
                    </Button>
                  </Col>

                  {canUseMarketScope && (
                    <Col md={2}>
                      <Form.Label className="fw-semibold mb-1 small">
                        <Shop size={12} className="me-1" />
                        Périmètre
                      </Form.Label>
                      <Form.Select
                        size="sm"
                        value={marketScope}
                        onChange={(e) => setMarketScope(e.target.value)}
                        disabled={loadingFilter}
                      >
                        <option value="all">Tous</option>
                        <option value="no_market">Hors marché</option>
                        <option value="market">De marché</option>
                      </Form.Select>
                    </Col>
                  )}
                </Row>

                {(dateRange.startDate || dateRange.endDate) && (
                  <div className="mt-2 text-center">
                    <Badge bg="info" className="px-3 py-2">
                      <Calendar className="me-1" size={12} />
                      Période : {dateRange.startDate || "Début"} → {dateRange.endDate || "Aujourd'hui"}
                    </Badge>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {error && (
          <Card className="border-0 shadow-sm mb-4 overflow-hidden">
            <Card.Body className="p-4">
              <div className="d-flex align-items-start">
                <div
                  className="flex-shrink-0 bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: "48px", height: "48px" }}
                >
                  <ExclamationCircle size={24} className="text-warning" />
                </div>
                <div className="flex-grow-1 ms-3">
                  <h5 className="fw-bold mb-2 text-warning">Attention</h5>
                  <p className="mb-2 text-dark">{error}</p>
                  <Button variant="outline-warning" size="sm" onClick={() => setError(null)}
                    className="rounded-pill px-3">
                    <X size={14} className="me-2" /> Fermer
                  </Button>
                  <Button variant="warning" size="sm" onClick={() => loadDashboardData(true)}
                    className="rounded-pill px-3 ms-2">
                    <Repeat size={14} className="me-2" /> Réessayer
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Cartes de Statistiques */}
        <Row className="g-3 mb-4">
          <Col md={6} lg={3}>
            <StatCard
              title="Contribuables"
              value={stats.totalContribuables.toLocaleString()}
              icon={People}
              color="primary"
              description={`${stats.registeredToday} nouveaux aujourd'hui`}
              loading={loadingStats || loadingFilter}
            />
          </Col>
          <Col md={6} lg={3}>
            <StatCard
              title="Taxe Prévisionnelle"
              value={formatCurrency(stats.totalAttendu)}
              icon={CurrencyDollar}
              color="info"
              description={`${stats.echeancesPayees} soldées • ${stats.echeancesEnAttente} en attente`}
              loading={loadingStats || loadingFilter}
            />
          </Col>
          <Col md={6} lg={3}>
            <StatCard
              title="Total des Taxes Collectées"
              value={formatCurrency(stats.totalPaye)}
              icon={Cash}
              color="success"
              trend={stats.tauxRecouvrement >= 80 ? "up" : "down"}
              trendValue={`${stats.tauxRecouvrement}%`}
              description={`Sur ${formatCurrency(stats.totalAttendu)} attendus`}
              loading={loadingStats || loadingFilter}
            />
          </Col>
          <Col md={6} lg={3}>
            <StatCard
              title="Taxes Non Collectées"
              value={formatCurrency(stats.totalRestant)}
              icon={Receipt}
              color="warning"
              description={`${stats.echeancesPartielles} partielles • ${stats.echeancesEnAttente} en attente`}
              loading={loadingStats || loadingFilter}
            />
          </Col>
        </Row>

        {/* Statistiques par quartier */}
        <Row className="g-4 mb-4">
          <Col xs={12}>
            <Card className="border-0 shadow-sm overflow-hidden"
              style={{ borderRadius: "18px", background: "#fff" }}>
              <Card.Header className="bg-white border-0 py-3 py-lg-4 px-3 px-lg-4"
                style={{ borderBottom: "1px solid #eef1f5" }}>
                <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3">
                  <div className="d-flex align-items-center">
                    <div className="d-flex align-items-center justify-content-center rounded-4 me-3"
                      style={{
                        width: "46px", height: "46px",
                        background: "linear-gradient(135deg, rgba(13,110,253,.12), rgba(13,110,253,.04))",
                      }}>
                      <GeoAlt className="text-primary" size={23} />
                    </div>
                    <div>
                      <h5 className="fw-bold mb-1" style={{ color: "#1e293b" }}>
                        Statistiques détaillées par quartier
                      </h5>
                      <div className="d-flex align-items-center flex-wrap gap-2">
                        <small className="text-muted">Vue globale des contribuables, taxes et recouvrements</small>
                        <Badge bg="primary" className="rounded-pill px-2 py-1" style={{ fontSize: "10px" }}>
                          {quartierStats.length} quartier(s)
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex flex-column flex-md-row align-items-md-center gap-2">
                    <ButtonGroup size="sm" className="flex-wrap">
                      <Button
                        variant={quartierStatsOrder === "total" ? "primary" : "outline-primary"}
                        onClick={() => handleQuartierStatsOrderChange("total")}
                      >
                        Total
                      </Button>
                      <Button
                        variant={quartierStatsOrder === "collecte" ? "success" : "outline-success"}
                        onClick={() => handleQuartierStatsOrderChange("collecte")}
                      >
                        Collecté
                      </Button>
                      <Button
                        variant={quartierStatsOrder === "reste" ? "danger" : "outline-danger"}
                        onClick={() => handleQuartierStatsOrderChange("reste")}
                      >
                        Reste
                      </Button>
                      <Button
                        variant={quartierStatsOrder === "contribuables" ? "dark" : "outline-dark"}
                        onClick={() => handleQuartierStatsOrderChange("contribuables")}
                      >
                        <People className="me-1" />
                        Contribuables
                      </Button>
                      <Button
                        variant={quartierStatsOrder === "taux" ? "warning" : "outline-warning"}
                        onClick={() => handleQuartierStatsOrderChange("taux")}
                      >
                        Taux
                      </Button>
                    </ButtonGroup>

                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={loadQuartierStats}
                      disabled={loadingQuartierStats}
                      className="d-flex align-items-center justify-content-center"
                      style={{ minWidth: "42px", height: "32px" }}
                    >
                      {loadingQuartierStats ? <Spinner animation="border" size="sm" /> : (
                        <>
                          <ArrowRepeat size={16} className="me-1" />
                          <span>Actualiser</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Card.Header>

              <Card.Body className="p-3 p-lg-4">
                {loadingQuartierStats ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted mb-0">Chargement des statistiques par quartier...</p>
                  </div>
                ) : quartierStats.length === 0 ? (
                  <div className="text-center py-5">
                    <InfoCircle size={55} className="text-muted opacity-25" />
                    <p className="mt-3 text-muted mb-0">Aucune donnée disponible</p>
                  </div>
                ) : (
                  <>
                    <Row className="g-3 mb-4">
                      <Col md={6} xl={3}>
                        <div className="p-3 rounded-4 h-100"
                          style={{
                            background: "rgba(13,110,253,.06)",
                            border: "1px solid rgba(13,110,253,.10)",
                          }}>
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <small className="text-muted fw-semibold">Quartiers</small>
                              <h4 className="fw-bold mb-0 mt-1 text-primary">
                                {quartierStats.length.toLocaleString("fr-FR")}
                              </h4>
                            </div>
                            <GeoAlt size={30} className="text-primary opacity-50" />
                          </div>
                        </div>
                      </Col>
                      <Col md={6} xl={3}>
                        <div className="p-3 rounded-4 h-100"
                          style={{
                            background: "rgba(25,135,84,.06)",
                            border: "1px solid rgba(25,135,84,.10)",
                          }}>
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <small className="text-muted fw-semibold">Contribuables</small>
                              <h4 className="fw-bold mb-0 mt-1 text-success">
                                {quartierStats
                                  .reduce((sum, q) => sum + (q.contribuables || 0), 0)
                                  .toLocaleString("fr-FR")}
                              </h4>
                            </div>
                            <People size={30} className="text-success opacity-50" />
                          </div>
                        </div>
                      </Col>
                      <Col md={6} xl={3}>
                        <div className="p-3 rounded-4 h-100"
                          style={{
                            background: "rgba(13,202,240,.06)",
                            border: "1px solid rgba(13,202,240,.10)",
                          }}>
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <small className="text-muted fw-semibold">Taxe totale</small>
                              <h6 className="fw-bold mb-0 mt-1 text-info" style={{ fontSize: "17px" }}>
                                {formatCurrency(
                                  quartierStats.reduce((sum, q) => sum + (q.totalTaxe || 0), 0)
                                )}
                              </h6>
                            </div>
                            <CurrencyDollar size={30} className="text-info opacity-50" />
                          </div>
                        </div>
                      </Col>
                      <Col md={6} xl={3}>
                        <div className="p-3 rounded-4 h-100"
                          style={{
                            background: "rgba(255,193,7,.08)",
                            border: "1px solid rgba(255,193,7,.15)",
                          }}>
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <small className="text-muted fw-semibold">Total collecté</small>
                              <h6 className="fw-bold mb-0 mt-1 text-warning" style={{ fontSize: "17px" }}>
                                {formatCurrency(
                                  quartierStats.reduce((sum, q) => sum + (q.totalCollecte || 0), 0)
                                )}
                              </h6>
                            </div>
                            <CashStack size={30} className="text-warning opacity-50" />
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg={6}>
                        <Card className="border mb-4 overflow-hidden h-100"
                          style={{ borderRadius: "16px", borderColor: "#edf0f4" }}>
                          <Card.Header className="bg-white border-0 px-3 px-lg-4 pt-3">
                            <div className="d-flex align-items-center">
                              <div className="rounded-3 d-flex align-items-center justify-content-center me-2"
                                style={{ width: "38px", height: "38px", background: "rgba(13,110,253,.10)" }}>
                                <BarChart className="text-primary" size={19} />
                              </div>
                              <div>
                                <h6 className="fw-bold mb-0">Situation financière par quartier</h6>
                                <small className="text-muted">
                                  Comparaison entre les montants prévus, collectés et restants
                                </small>
                              </div>
                            </div>
                          </Card.Header>
                          <Card.Body className="p-2 p-lg-3">
                            <ReactApexChart
                              options={quartierTaxChartOptions}
                              series={quartierTaxSeries}
                              type="bar"
                            />
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col lg={6}>
                        <Card className="border mb-4 overflow-hidden h-100"
                          style={{ borderRadius: "16px", borderColor: "#edf0f4" }}>
                          <Card.Header className="bg-white border-0 px-3 px-lg-4 pt-3">
                            <div className="d-flex align-items-center">
                              <div className="rounded-3 d-flex align-items-center justify-content-center me-2"
                                style={{ width: "38px", height: "38px", background: "rgba(155,89,182,.10)" }}>
                                <People style={{ color: "#9b59b6" }} size={19} />
                              </div>
                              <div>
                                <h6 className="fw-bold mb-0">Répartition des contribuables par quartier</h6>
                                <small className="text-muted">
                                  Nombre total de contribuables enregistrés dans chaque quartier
                                </small>
                              </div>
                            </div>
                          </Card.Header>
                          <Card.Body className="p-2 p-lg-3">
                            <ReactApexChart
                              options={quartierContribuablesChartOptions}
                              series={quartierContribuablesSeries}
                              type="bar"
                            />
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>

                    <Card className="border overflow-hidden"
                      style={{ borderRadius: "16px", borderColor: "#edf0f4" }}>
                      <Card.Header className="bg-white border-0 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-3 px-3 px-lg-4">
                        <div>
                          <h6 className="fw-bold mb-0">Détail des statistiques par quartier</h6>
                          <small className="text-muted">
                            Liste complète des montants et du nombre de contribuables
                          </small>
                        </div>

                        <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">
                          {quartierStats.length} résultat(s)
                        </Badge>

                        <Button
                          variant="success"
                          size="sm"
                          onClick={exportQuartierStatsToExcel}
                          disabled={!quartierStats || quartierStats.length === 0}
                          className="d-flex align-items-center gap-2 px-3"
                          style={{ borderRadius: "8px", whiteSpace: "nowrap" }}
                        >
                          <FileEarmarkExcel size={16} />
                          <span>Exporter Excel</span>
                        </Button>
                      </Card.Header>

                      <div className="table-responsive" style={{ maxHeight: "650px", overflowY: "auto" }}>
                        <Table hover className="mb-0 align-middle">
                          <thead className="sticky-top" style={{ zIndex: 1, background: "#f8fafc" }}>
                            <tr>
                              <th className="text-center text-muted fw-semibold" style={{ width: "65px" }}>#</th>
                              <th className="fw-semibold" style={{ width: "80px" }}>
                                <GeoAlt className="me-1 text-primary" />
                                Quartier
                              </th>
                              <th className="text-center fw-semibold" style={{ width: "145px" }}>
                                <People className="me-1 text-success" />
                                Nb. contribuables
                              </th>
                              <th className="text-end fw-semibold" style={{ width: "120px" }}>Taxe prévue</th>
                              <th className="text-end fw-semibold" style={{ width: "120px" }}>Collecté</th>
                              <th className="text-end fw-semibold" style={{ width: "120px" }}>Reste à payer</th>
                              <th className="text-center fw-semibold" style={{ width: "120px" }}>Taux</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quartierStats.map((stat, idx) => {
                              const taux = stat.tauxRecouvrement || 0;
                              return (
                                <tr key={stat.quartier_id || idx}>
                                  <td className="text-center text-muted fw-semibold">{idx + 1}</td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                        style={{ width: "34px", height: "34px", background: "rgba(13,110,253,.08)" }}>
                                        <GeoAlt size={15} className="text-primary" />
                                      </div>
                                      <span className="fw-semibold text-dark">{stat.quartier_nom}</span>
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    <Badge bg="success" className="rounded-pill px-3 py-2"
                                      style={{ fontSize: "12px", minWidth: "65px" }}>
                                      <People className="me-1" size={13} />
                                      {stat.contribuables.toLocaleString("fr-FR")}
                                    </Badge>
                                  </td>
                                  <td className="text-end">
                                    <span className="fw-semibold text-primary">
                                      {formatCurrency(stat.totalTaxe)}
                                    </span>
                                  </td>
                                  <td className="text-end">
                                    <span className="fw-semibold text-success">
                                      {formatCurrency(stat.totalCollecte)}
                                    </span>
                                  </td>
                                  <td className="text-end">
                                    <span className="fw-semibold text-danger">
                                      {formatCurrency(stat.totalReste)}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    <div className="d-flex align-items-center justify-content-center gap-2"
                                      style={{ minWidth: "60px" }}>
                                      <ProgressBar
                                        now={Math.min(taux, 100)}
                                        variant={taux >= 80 ? "success" : taux >= 50 ? "warning" : "danger"}
                                        style={{ width: "35px", height: "7px" }}
                                      />
                                      <Badge
                                        bg={taux >= 80 ? "success" : taux >= 50 ? "warning" : "danger"}
                                        className="rounded-pill"
                                      >
                                        {taux.toFixed(1)}%
                                      </Badge>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>

                      <Card.Footer className="bg-white border-0 px-3 px-lg-4 py-3"
                        style={{ borderTop: "1px solid #eef1f5" }}>
                        <small className="text-muted">
                          <InfoCircle className="me-1" />
                          Affichage de tous les {quartierStats.length} quartiers disponibles.
                        </small>
                      </Card.Footer>
                    </Card>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Graphique principal */}
        <Row className="g-4 mb-4">
          <Col lg={8}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-white border-bottom">
                <Card.Title as="h6" className="mb-0">
                  <BarChart className="me-2" />
                  Évolution des collectes
                </Card.Title>
                <small className="text-muted">
                  Comparaison objectifs vs montants payés sur les échéances
                </small>
              </Card.Header>
              <Card.Body>
                {loadingCharts || loadingFilter ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Chargement...</p>
                  </div>
                ) : monthlyRevenue.series?.length > 0 && monthlyRevenue.series[0]?.data?.length > 0 ? (
                  <>
                    <ReactApexChart
                      options={revenueChartOptions}
                      series={monthlyRevenue.series}
                      type="area"
                      height={550}
                    />
                    {stats.totalAttendu > 0 && (
                      <div className="mt-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <span className="text-success fw-bold">
                              {formatCurrency(stats.totalPaye)} collectés
                            </span>
                            <span className="text-muted mx-2">sur</span>
                            <span className="text-primary fw-bold">
                              {formatCurrency(stats.totalAttendu)} attendus
                            </span>
                            <span className="text-muted mx-2">•</span>
                            <span className="text-warning fw-bold">
                              {formatCurrency(stats.totalRestant)} restants
                            </span>
                          </div>
                          <Badge bg={stats.tauxRecouvrement >= 80 ? "success" : "warning"}
                            className="rounded-pill">
                            {stats.tauxRecouvrement}% recouvré
                          </Badge>
                        </div>
                        <ProgressBar className="mt-2" style={{ height: "8px", borderRadius: "4px" }}>
                          <ProgressBar
                            variant="success"
                            now={(stats.totalPaye / stats.totalAttendu) * 100}
                            key={1}
                          />
                          <ProgressBar
                            variant="warning"
                            now={(stats.totalRestant / stats.totalAttendu) * 100}
                            key={2}
                          />
                        </ProgressBar>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted">Aucune donnée disponible</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <Row className="g-4">
              <Col md={12}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Header className="bg-white border-bottom">
                    <Card.Title as="h6" className="mb-0">
                      <Activity className="me-2" />
                      Taux de Recouvrement
                    </Card.Title>
                  </Card.Header>
                  <Card.Body className="text-center">
                    {loadingCharts || loadingFilter ? (
                      <div className="py-5">
                        <Spinner animation="border" variant="primary" />
                      </div>
                    ) : (
                      <>
                        <ReactApexChart
                          options={performanceChartOptions}
                          series={performanceChartSeries}
                          type="radialBar"
                          height={200}
                        />
                        <div className="mt-4">
                          <h4 className="mb-2">{stats.tauxRecouvrement}% recouvré</h4>
                          <ProgressBar now={stats.tauxRecouvrement} variant="success" className="mb-3" />
                          <div className="row text-center">
                            <div className="col-6">
                              <h5 className="text-success mb-1">{stats.echeancesPayees}</h5>
                              <small>Échéances payées</small>
                            </div>
                            <div className="col-6">
                              <h5 className="text-warning mb-1">{stats.echeancesEnAttente}</h5>
                              <small>Échéances en attente</small>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col md={12}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Header className="bg-white border-bottom">
                    <Card.Title as="h6" className="mb-0">
                      <GraphUp className="me-2" />
                      Nouveaux Contribuables
                    </Card.Title>
                  </Card.Header>
                  <Card.Body>
                    {loadingCharts || loadingFilter ? (
                      <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                      </div>
                    ) : (
                      <>
                        <ReactApexChart
                          options={growthChartOptions}
                          series={contribuableGrowth.series}
                          type="line"
                          height={200}
                        />
                        <div className="mt-3 text-center">
                          <Badge bg="success" className="px-3 py-2 rounded-pill">
                            <ArrowUpRight className="me-1" />
                            {stats.registeredToday} nouveaux aujourd'hui
                          </Badge>
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Distribution et Activités */}
        <Row className="g-4 mb-4">
          <Col lg={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
                <div>
                  <Card.Title as="h6" className="mb-0">
                    <PieChart className="me-2" />
                    Distribution des Taxes
                  </Card.Title>
                  <small className="text-muted">
                    <span className="text-success fw-bold">
                      {formatCurrency(totalTaxAmounts.paid)} payés
                    </span>{" "}
                    sur{" "}
                    <span className="text-primary fw-bold">
                      {formatCurrency(totalTaxAmounts.expected)} attendus
                    </span>
                  </small>
                </div>
                <Button variant="outline-primary" size="sm"
                  as={Link} to="/contribuables/CollectesUsagers" className="rounded-pill">
                  <Eye className="me-1" />
                  Voir détails
                </Button>
              </Card.Header>
              <Card.Body>
                {loadingCharts || loadingFilter ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : taxDistribution.length > 0 ? (
                  <div className="row">
                    <div className="col-7">
                      <ReactApexChart
                        options={{
                          chart: { type: "donut" },
                          labels: taxDistribution.map((t) => t.type),
                          colors: taxDistribution.map((t) => t.color),
                          legend: { show: false },
                          dataLabels: { enabled: false },
                        }}
                        series={taxDistribution.map((t) => t.amount)}
                        type="donut"
                        height={250}
                      />
                    </div>
                    <div className="col-5">
                      {taxDistribution.map((tax, idx) => (
                        <div key={idx} className="mb-3">
                          <div className="d-flex align-items-center mb-1">
                            <div className="rounded-circle me-2"
                              style={{ width: "10px", height: "10px", backgroundColor: tax.color }} />
                            <span className="fw-medium">{tax.type}</span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <div>
                              <span className="text-success fw-bold">
                                {formatCurrency(tax.amount)}
                              </span>
                              <span className="text-muted mx-1">/</span>
                              <span className="text-primary">
                                {formatCurrency(tax.amountExpected)}
                              </span>
                            </div>
                            <Badge bg={tax.percentage >= 100 ? "success" : "warning"}
                              className="rounded-pill">
                              {tax.percentage}%
                            </Badge>
                          </div>
                          <ProgressBar
                            now={tax.percentage}
                            variant={tax.percentage >= 100 ? "success" : "warning"}
                            style={{ height: "4px" }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted">Aucune donnée disponible</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
                <div>
                  <Card.Title as="h6" className="mb-0">
                    <Clock className="me-2" />
                    Activités Récentes
                  </Card.Title>
                  <small className="text-muted">
                    Les {recentActivities.length} derniers paiements
                  </small>
                </div>
                <Button variant="outline-primary" size="sm"
                  as={Link} to="/contribuables/CollectesUsagers" className="rounded-pill">
                  Voir tout
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                {loadingActivities || loadingFilter ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : recentActivities.length > 0 ? (
                  <div className="list-group list-group-flush">
                    {recentActivities.map((activity) => (
                      <div key={activity.id} className="list-group-item border-0 border-bottom">
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0">
                            <div className={`bg-${activity.status}-subtle p-2 rounded-circle`}>
                              <CurrencyDollar />
                            </div>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <div className="d-flex justify-content-between">
                              <h6 className="mb-0">{activity.user}</h6>
                              <small className="text-muted">{activity.time}</small>
                            </div>
                            <p className="mb-1 small">{activity.action}</p>
                            {activity.amount && (
                              <div className="d-flex justify-content-between">
                                <span className="text-success fw-medium">{activity.amount}</span>
                                {getStatusBadge(activity.status)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted">Aucune activité récente</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Top Contribuables */}
        <Row className="g-4">
          <Col lg={8}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-white border-bottom">
                <Card.Title as="h6" className="mb-0">
                  <People className="me-2" />
                  Top {topTaxpayers.length} Contribuables
                </Card.Title>
                <small className="text-muted">
                  Classement par montant total initial (incluant les sous-comptes)
                </small>
              </Card.Header>
              <Card.Body className="p-0">
                {loadingTopContribuables || loadingFilter ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Chargement...</p>
                  </div>
                ) : topTaxpayers.length > 0 ? (
                  <Table hover responsive className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>Contribuable</th>
                        <th>Type</th>
                        <th>Montant Payé</th>
                        <th>Montant Initial</th>
                        <th>Progression</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topTaxpayers.map((taxpayer, idx) => (
                        <tr key={taxpayer.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                                style={{ width: "36px", height: "36px" }}>
                                {idx === 0 ? (
                                  <Trophy size={16} className="text-warning" />
                                ) : (
                                  <Building className="text-primary" size={16} />
                                )}
                              </div>
                              <div>
                                <h6 className="mb-0">{taxpayer.name}</h6>
                                <small className="text-muted">
                                  {taxpayer.paymentCount} paiement(s)
                                  {taxpayer.subAccountsCount > 0 &&
                                    ` • ${taxpayer.subAccountsCount} activité(s)`}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <Badge bg="light" text="dark" className="fw-normal rounded-pill">
                              {taxpayer.type}
                            </Badge>
                          </td>
                          <td className="fw-bold text-success">{taxpayer.amountPaid}</td>
                          <td className="text-muted">{taxpayer.amountExpected}</td>
                          <td style={{ width: "150px" }}>
                            <div className="d-flex align-items-center">
                              <ProgressBar className="flex-grow-1 me-2" style={{ height: "6px" }}>
                                <ProgressBar
                                  now={taxpayer.percentagePaid}
                                  variant={
                                    taxpayer.percentagePaid >= 100 ? "success"
                                    : taxpayer.percentagePaid >= 50 ? "warning" : "danger"
                                  }
                                />
                              </ProgressBar>
                              <small className="text-muted">{taxpayer.percentagePaid}%</small>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted">Aucun contribuable trouvé</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-white border-bottom">
                <Card.Title as="h6" className="mb-0">
                  <Globe className="me-2" />
                  Méthodes de Paiement
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {loadingCharts || loadingFilter ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : paymentMethods.length > 0 ? (
                  <>
                    <ReactApexChart
                      options={deviceUsageOptions}
                      series={deviceUsageSeries}
                      type="donut"
                      height={250}
                    />
                    <div className="mt-3">
                      {paymentMethods.map((method, idx) => (
                        <div key={idx} className="mb-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              {getPaymentMethodIcon(method.method)}
                              <span>{method.method}</span>
                            </div>
                            <div className="d-flex flex-column align-items-end">
                              <span className="fw-bold">{method.count} trans.</span>
                              <small className="text-muted">{method.amount}</small>
                            </div>
                          </div>
                          <ProgressBar
                            now={method.percentage}
                            variant="success"
                            style={{ height: "3px" }}
                            className="mt-1"
                          />
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted">Aucune donnée disponible</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </React.Fragment>
  );
}